import { useEffect, useRef } from 'react';

const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

/**
 * Subscribe to the backend SSE event stream.
 * Calls `onEvent({type, ...payload})` for each event.
 *
 * Reconnection policy (Phase 6.2.3 fix):
 *   - Flat 3s retry was causing endless 401 spam in prod logs when a stale
 *     tab's session expired. EventSource auto-reconnects on `onerror` and
 *     doesn't expose the HTTP status code, so we have to detect dead
 *     sessions out-of-band.
 *   - Now: exponential backoff (3 → 6 → 12 → 24 → 30s cap). After 3
 *     consecutive failures, probe `/api/auth/me`. If the probe also returns
 *     401, the session is dead — stop retrying entirely and emit a
 *     `stream.session_expired` event so the UI can prompt re-login.
 *   - Auth probe uses `credentials: include` (same cookie that drives SSE).
 *
 * Note: EventSource sends cookies automatically on same-origin requests.
 * Our backend also accepts `?token=` query fallback — not needed here.
 */
export function useSSE(onEvent, enabled = true) {
  const handlerRef = useRef(onEvent);
  handlerRef.current = onEvent;

  useEffect(() => {
    if (!enabled) return undefined;
    let es = null;
    let closed = false;
    let reconnectTimer = null;
    let probeInFlight = false;
    let failureCount = 0;
    let gaveUp = false;

    const computeBackoff = (n) => {
      // 3 → 6 → 12 → 24 → 30s cap
      const ms = Math.min(3_000 * Math.pow(2, Math.max(0, n - 1)), 30_000);
      return ms;
    };

    const probeAuth = async () => {
      if (probeInFlight) return null;
      probeInFlight = true;
      try {
        const r = await fetch(`${API_URL}/api/auth/me`, {
          credentials: 'include',
          // Don't cache — we need the live session state
          cache: 'no-store',
        });
        return r.status;
      } catch (_e) {
        // Network-level failure — treat as transient, keep retrying SSE
        return null;
      } finally {
        probeInFlight = false;
      }
    };

    const giveUp = () => {
      gaveUp = true;
      try {
        handlerRef.current?.({ type: 'stream.session_expired' });
      } catch (_e) {
        // swallow
      }
    };

    const connect = () => {
      if (closed || gaveUp) return;
      try {
        es = new EventSource(`${API_URL}/api/events/stream`, { withCredentials: true });
        es.addEventListener('ready', () => {
          // Connection succeeded — reset the failure counter
          failureCount = 0;
          try {
            handlerRef.current?.({ type: 'stream.ready' });
          } catch (_err) {
            // ignore
          }
        });
        es.onmessage = (e) => {
          try {
            const data = JSON.parse(e.data);
            handlerRef.current?.(data);
          } catch (_err) {
            // ignore malformed
          }
        };
        es.onerror = async () => {
          if (closed || gaveUp) return;
          es?.close();
          es = null;
          failureCount += 1;

          // After 3 consecutive failures, probe whether session is alive.
          if (failureCount === 3) {
            const status = await probeAuth();
            if (status === 401 || status === 403) {
              giveUp();
              return;
            }
            // status 200 or null (network blip) → keep trying with backoff
          }

          // Safety net: hard cap at 8 attempts (≈2 min). Anything beyond is
          // almost certainly a permanent issue (revoked account, server down).
          if (failureCount >= 8) {
            giveUp();
            return;
          }
          reconnectTimer = setTimeout(connect, computeBackoff(failureCount));
        };
      } catch (_err) {
        failureCount += 1;
        reconnectTimer = setTimeout(connect, computeBackoff(failureCount));
      }
    };

    connect();

    // Phase 7-lite — iOS PWA backgrounding closes the SSE socket silently.
    // When the user resumes the app (visibilitychange → visible OR window focus)
    // force a reconnect so live updates resume immediately.
    const onResume = () => {
      if (closed) return;
      if (document.visibilityState !== 'visible') return;
      // If we gave up earlier (session was dead), check whether the user
      // signed back in. If so, reset and reconnect; otherwise stay quiet.
      if (gaveUp) {
        probeAuth().then((status) => {
          if (status && status < 400) {
            gaveUp = false;
            failureCount = 0;
            connect();
          }
        });
        return;
      }
      if (!es || es.readyState !== 1 /* OPEN */) {
        if (reconnectTimer) {
          clearTimeout(reconnectTimer);
          reconnectTimer = null;
        }
        try { es?.close(); } catch (_e) { /* ignore */ }
        es = null;
        failureCount = 0;
        connect();
      }
    };
    document.addEventListener('visibilitychange', onResume);
    window.addEventListener('focus', onResume);

    return () => {
      closed = true;
      document.removeEventListener('visibilitychange', onResume);
      window.removeEventListener('focus', onResume);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (es) es.close();
    };
  }, [enabled]);
}
